import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { arrayMove, rectSortingStrategy, SortableContext, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Trash, Upload } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Sku } from '@/interfaces/catalog/Sku'
import { extractErrorMessage } from '@/lib/api/errorUtils'
import { deleteSkuImage, reorderSkuImages, uploadSkuImages } from '@/lib/catalog/skuImages'
import { cn } from '@/lib/utils'

const MAX_IMAGES = 20

type SkuImage = Sku['images'][number]

type SkuGalleryProps = {
  sku: Sku
  onChange: () => void
}

const SortableImage = ({ image, isTitle, onDelete }: { image: SkuImage; isTitle: boolean; onDelete: () => void }) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'catalog.products.detail.gallery' })
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: image.id })

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn('group relative aspect-square overflow-hidden rounded-md border bg-muted', isDragging && 'z-10 opacity-70')}
    >
      <img
        src={image.url}
        alt=""
        className="size-full cursor-grab object-cover"
        draggable={false}
        {...attributes}
        {...listeners}
      />
      {isTitle && (
        <Badge className="absolute left-1 top-1">{t('titleImage')}</Badge>
      )}
      <Button
        type="button"
        variant="destructive"
        size="icon"
        className="absolute right-1 top-1 size-7 opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
        aria-label={t('delete')}
        onClick={onDelete}
      >
        <Trash className="size-4" />
      </Button>
    </div>
  )
}

export const SkuGallery = ({ sku, onChange }: SkuGalleryProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'catalog.products.detail.gallery' })
  // Local copy so a drag shows the new order before the server confirms it
  const [images, setImages] = useState(sku.images)
  const [isUploading, setIsUploading] = useState(false)
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  useEffect(() => setImages(sku.images), [sku.images])

  const run = async (request: Promise<unknown>) => {
    try {
      await request
    } catch (error) {
      toast.error(extractErrorMessage(error) ?? t('error'))
    } finally {
      onChange()
    }
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'image/*': [] },
    disabled: isUploading || images.length >= MAX_IMAGES,
    onDrop: async (files) => {
      if (files.length === 0) return
      setIsUploading(true)
      await run(uploadSkuImages(sku.id, files))
      setIsUploading(false)
    },
  })

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return
    const reordered = arrayMove(
      images,
      images.findIndex((image) => image.id === active.id),
      images.findIndex((image) => image.id === over.id),
    )
    setImages(reordered)
    run(reorderSkuImages(sku.id, reordered.map((image) => image.id)))
  }

  const isInherited = images.length === 0 && sku.gallery.length > 0

  return (
    <div className="space-y-4">
      {isInherited && (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">{t('inherited')}</p>
          <div className="grid grid-cols-3 gap-2 opacity-50 sm:grid-cols-4 md:grid-cols-6">
            {sku.gallery.map((image) => (
              <img key={image.id} src={image.url} alt="" className="aspect-square rounded-md border object-cover" />
            ))}
          </div>
        </div>
      )}

      {images.length > 0 && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={images.map((image) => image.id)} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6">
              {images.map((image, index) => (
                <SortableImage
                  key={image.id}
                  image={image}
                  isTitle={index === 0}
                  onDelete={() => run(deleteSkuImage(sku.id, image.id))}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <div
        {...getRootProps()}
        className={cn(
          'flex cursor-pointer flex-col items-center gap-2 rounded-md border-2 border-dashed p-6 text-center text-sm text-muted-foreground',
          isDragActive && 'border-primary bg-muted',
          (isUploading || images.length >= MAX_IMAGES) && 'cursor-not-allowed opacity-50',
        )}
      >
        <input {...getInputProps()} />
        <Upload className="size-6" />
        <p>{isUploading ? t('uploading') : t('dropzone', { max: MAX_IMAGES })}</p>
        {images.length > 1 && <p className="text-xs">{t('reorderHint')}</p>}
      </div>
    </div>
  )
}
